/**
 * Treatment-plan autosave — adapts the chart-document editor state to the
 * generic provider-note autosave hook (`useProviderNoteAutosave`), which diffs
 * normalized snapshots to decide when a silent draft save is needed.
 */
import type {
  Questionnaire,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';
import type { NoteAutosaveSnapshot } from '../pages/appointments/utils/noteAutosaveSnapshot';
import { buildNestedQuestionnaireResponseItemsForSave } from '../pages/appointments/utils/questionnaireResponseShape';

export function buildTreatmentPlanAutosaveSnapshot(opts: {
  questionnaireId: string;
  episodeId: string | undefined;
  items: QuestionnaireResponseItem[] | undefined;
  /**
   * Template used to persist the draft. Diffing must compare the items that will
   * actually be written: saving reorders to questionnaire order, fills `text`, and
   * drops answer-less questions. Diffing raw form items against those reshaped
   * items never converges, so autosave re-fires forever.
   */
  questionnaire?: Questionnaire;
  existingItems?: QuestionnaireResponseItem[] | undefined;
}): NoteAutosaveSnapshot {
  const items = opts.questionnaire
    ? buildNestedQuestionnaireResponseItemsForSave(opts.questionnaire, opts.items, opts.existingItems)
      ?? opts.items
    : opts.items;
  return {
    // Episode is folded into templateId so the note snapshot diff treats an
    // episode change as a meaningful change without note-specific fields.
    templateId: `${opts.questionnaireId}::${opts.episodeId ?? ''}`,
    // Until the provider has entered real content, the form only holds
    // auto-populated dates and empty repeating-group scaffolding. Normalizing
    // that state to "empty" keeps autosave from creating a server draft for a
    // document the provider never actually touched.
    responseItems: treatmentPlanHasProviderContent(items) ? items : [],
    metadata: null,
    attendanceByPatient: {},
    locationByPatient: {},
    comments: [],
  };
}

/** Assigned/expiration dates are auto-populated on open — not provider content. */
const AUTO_POPULATED_DATE_SUFFIXES = ['-date-assigned', '-expiration-date'];

function answerHasValue(answer: QuestionnaireResponseItemAnswer): boolean {
  return Object.entries(answer).some(([key, value]) => {
    if (!key.startsWith('value')) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return value !== undefined && value !== null;
  });
}

function itemHasProviderContent(item: QuestionnaireResponseItem): boolean {
  const isAutoPopulatedDate = AUTO_POPULATED_DATE_SUFFIXES.some(
    (suffix) => item.linkId?.endsWith(suffix),
  );
  if (!isAutoPopulatedDate && (item.answer ?? []).some(answerHasValue)) return true;
  return (item.item ?? []).some(itemHasProviderContent);
}

/**
 * True once the provider has actually entered content. Opening the editor
 * auto-populates dates and empty repeating-group scaffolding; those alone must
 * not create a server draft for a document the provider never touched.
 */
export function treatmentPlanHasProviderContent(
  items: QuestionnaireResponseItem[] | undefined,
): boolean {
  return (items ?? []).some(itemHasProviderContent);
}
